import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductionAnalyticsCard from '../../ProductionAnalyticsCard';
import ProductionHeatmap from './ProductionHeatmap';
import ProductionLineChart from './ProductionLineChart';
import { fetchHeatmapData, fetchLineData } from './productionDataAPI';
import { colors } from '../../theme';
import { normalizeMonth } from './monthUtils';

const VIEW_OPTIONS = ['data', 'heatmap', 'line'];
const BASELINE_OPTIONS = [6, 12, 24];
const DEFAULT_START = '2020-01';

const currentMonthKey = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const parseProductsParam = (param) => {
  if (!param) return [];
  return param.split(',').map(item => item.trim()).filter(Boolean);
};

const ensureRangeOrder = (start, end) => {
  if (start <= end) return [start, end];
  return [end, start];
};

const chipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  padding: '0.38rem 0.72rem',
  borderRadius: '999px',
  border: `1px solid ${colors.panelBorder}`,
  backgroundColor: 'rgba(70,187,137,0.12)',
  fontSize: '0.8rem',
  color: colors.textStrong,
  marginRight: '0.4rem',
  marginBottom: '0.4rem'
};

const ProductionData = ({
  stats,
  year,
  setYear,
  years,
  explain,
  setExplain
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const storedView = (() => {
    const param = searchParams.get('view');
    if (param && VIEW_OPTIONS.includes(param)) return param;
    const cached = window.localStorage.getItem('analyticsProductionView');
    if (cached && VIEW_OPTIONS.includes(cached)) return cached;
    return 'data';
  })();

  const storedBaseline = (() => {
    const rawParam = searchParams.get('baselineMonths') ?? searchParams.get('baseline');
    const param = Number(rawParam);
    if (BASELINE_OPTIONS.includes(param)) return param;
    const cached = Number(window.localStorage.getItem('analyticsProductionBaseline'));
    return BASELINE_OPTIONS.includes(cached) ? cached : 6;
  })();

  const storedStart = normalizeMonth(searchParams.get('startYearMonth') ?? searchParams.get('start'), window.localStorage.getItem('analyticsProductionStart') || DEFAULT_START);
  const storedEnd = normalizeMonth(searchParams.get('endYearMonth') ?? searchParams.get('end'), window.localStorage.getItem('analyticsProductionEnd') || currentMonthKey());

  const [view, setView] = useState(storedView);
  const [baseline, setBaseline] = useState(storedBaseline);
  const [startMonth, setStartMonth] = useState(storedStart);
  const [endMonth, setEndMonth] = useState(storedEnd);
  const [selectedProducts, setSelectedProducts] = useState(parseProductsParam(searchParams.get('products')));

  const [heatmapState, setHeatmapState] = useState({ data: null, loading: true, error: null });
  const [lineState, setLineState] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    window.localStorage.setItem('analyticsProductionView', view);
  }, [view]);

  useEffect(() => {
    window.localStorage.setItem('analyticsProductionBaseline', String(baseline));
  }, [baseline]);

  useEffect(() => {
    window.localStorage.setItem('analyticsProductionStart', startMonth);
  }, [startMonth]);

  useEffect(() => {
    window.localStorage.setItem('analyticsProductionEnd', endMonth);
  }, [endMonth]);

  useEffect(() => {
    const paramProducts = parseProductsParam(searchParams.get('products'));
    const paramView = searchParams.get('view');
    const paramBaseline = Number(searchParams.get('baselineMonths') ?? searchParams.get('baseline'));
    const paramStart = normalizeMonth(searchParams.get('startYearMonth') ?? searchParams.get('start'), startMonth);
    const paramEnd = normalizeMonth(searchParams.get('endYearMonth') ?? searchParams.get('end'), endMonth);

    if (paramView && VIEW_OPTIONS.includes(paramView) && paramView !== view) {
      setView(paramView);
    }
    if (BASELINE_OPTIONS.includes(paramBaseline) && paramBaseline !== baseline) {
      setBaseline(paramBaseline);
    }
    if (paramStart !== startMonth || paramEnd !== endMonth) {
      const [orderedStart, orderedEnd] = ensureRangeOrder(paramStart, paramEnd);
      if (orderedStart !== startMonth) setStartMonth(orderedStart);
      if (orderedEnd !== endMonth) setEndMonth(orderedEnd);
    }
    const productsEqual = paramProducts.length === selectedProducts.length && paramProducts.every((p, idx) => p === selectedProducts[idx]);
    if (!productsEqual) {
      setSelectedProducts(paramProducts);
    }
  }, [searchParams, baseline, endMonth, selectedProducts, startMonth, view]);

  const effectiveProducts = selectedProducts.length ? selectedProducts : undefined;

  useEffect(() => {
    let cancelled = false;
    setHeatmapState(prev => ({ ...prev, loading: true, error: null }));
    fetchHeatmapData({ windowMonths: baseline, startMonth, endMonth, products: effectiveProducts })
      .then(result => {
        if (cancelled) return;
        setHeatmapState({ data: result, loading: false, error: null });
      })
      .catch(error => {
        if (cancelled) return;
        setHeatmapState({ data: null, loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [baseline, startMonth, endMonth, effectiveProducts]);

  useEffect(() => {
    let cancelled = false;
    setLineState(prev => ({ ...prev, loading: true, error: null }));
    fetchLineData({ windowMonths: baseline, startMonth, endMonth, products: effectiveProducts })
      .then(result => {
        if (cancelled) return;
        setLineState({ data: result, loading: false, error: null });
      })
      .catch(error => {
        if (cancelled) return;
        setLineState({ data: null, loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [baseline, startMonth, endMonth, effectiveProducts]);

  const updateSearchParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value === null || value === undefined || value === '') {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    setSearchParams(params);
  };

  const handleViewChange = (nextView) => {
    if (!VIEW_OPTIONS.includes(nextView)) return;
    setView(nextView);
    updateSearchParam('view', nextView);
  };

  const handleBaselineChange = (event) => {
    const value = Number(event.target.value);
    if (!BASELINE_OPTIONS.includes(value)) return;
    setBaseline(value);
    updateSearchParam('baselineMonths', String(value));
    updateSearchParam('baseline', null);
  };

  const handleStartChange = (event) => {
    const month = normalizeMonth(event.target.value, startMonth);
    const [orderedStart, orderedEnd] = ensureRangeOrder(month, endMonth);
    setStartMonth(orderedStart);
    setEndMonth(orderedEnd);
    updateSearchParam('startYearMonth', orderedStart);
    updateSearchParam('endYearMonth', orderedEnd);
    updateSearchParam('start', null);
    updateSearchParam('end', null);
  };

  const handleEndChange = (event) => {
    const month = normalizeMonth(event.target.value, endMonth);
    const [orderedStart, orderedEnd] = ensureRangeOrder(startMonth, month);
    setStartMonth(orderedStart);
    setEndMonth(orderedEnd);
    updateSearchParam('startYearMonth', orderedStart);
    updateSearchParam('endYearMonth', orderedEnd);
    updateSearchParam('start', null);
    updateSearchParam('end', null);
  };

  const handleResetFilters = () => {
    const defaultEnd = currentMonthKey();
    setBaseline(6);
    setStartMonth(DEFAULT_START);
    setEndMonth(defaultEnd);
    setSelectedProducts([]);
    updateSearchParam('baselineMonths', '6');
    updateSearchParam('baseline', null);
    updateSearchParam('startYearMonth', DEFAULT_START);
    updateSearchParam('endYearMonth', defaultEnd);
    updateSearchParam('start', null);
    updateSearchParam('end', null);
    updateSearchParam('products', null);
  };

  const handleSelectProduct = (productNumber, { multi } = {}) => {
    setView('line');
    updateSearchParam('view', 'line');
    setSelectedProducts(prev => {
      if (multi) {
        if (prev.includes(productNumber)) {
          const filtered = prev.filter(item => item !== productNumber);
          updateSearchParam('products', filtered.length ? filtered.join(',') : null);
          return filtered;
        }
        const next = [...prev, productNumber];
        updateSearchParam('products', next.join(','));
        return next;
      }
      const next = prev.length === 1 && prev[0] === productNumber ? [] : [productNumber];
      updateSearchParam('products', next.length ? next.join(',') : null);
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedProducts([]);
    updateSearchParam('products', null);
  };

  const heatmapData = useMemo(() => heatmapState.data, [heatmapState.data]);
  const lineData = useMemo(() => lineState.data, [lineState.data]);

  return (
    <div style={{ marginTop: '1rem' }}>
      <div className="ni-panel ni-panel--dense" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'inline-flex', borderRadius: '999px', border: `1px solid ${colors.panelBorder}`, overflow: 'hidden', background: 'rgba(255,255,255,0.02)' }}>
          {VIEW_OPTIONS.map(option => (
            <button
              key={option}
              type="button"
              onClick={() => handleViewChange(option)}
              style={{
                padding: '0.45rem 1rem',
                border: 'none',
                backgroundColor: view === option ? colors.accent : 'transparent',
                color: view === option ? '#11201c' : colors.textBody,
                cursor: 'pointer',
                fontWeight: view === option ? 600 : 500
              }}
            >
              {option === 'data' ? 'Data' : option === 'heatmap' ? 'Heatmap' : 'Overlayed Line Chart'}
            </button>
          ))}
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: colors.textBody }}>
          Rolling baseline:
          <select className="ni-select" value={baseline} onChange={handleBaselineChange} style={{ minWidth: '140px' }}>
            {BASELINE_OPTIONS.map(option => (
              <option key={option} value={option}>{option} months</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: colors.textBody }}>
          Start month:
          <input className="ni-input" type="month" value={startMonth} max={endMonth} onChange={handleStartChange} style={{ width: 'auto' }} />
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: colors.textBody }}>
          End month:
          <input className="ni-input" type="month" value={endMonth} min={startMonth} max={currentMonthKey()} onChange={handleEndChange} style={{ width: 'auto' }} />
        </label>
        <button
          className="ni-button ni-button--ghost"
          type="button"
          onClick={handleResetFilters}
        >
          Reset
        </button>
      </div>

      {selectedProducts.length ? (
        <div style={{ marginBottom: '0.75rem' }}>
          {selectedProducts.map(product => (
            <span key={product} style={chipStyle}>{product}</span>
          ))}
          <button
            type="button"
            onClick={handleClearSelection}
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.danger,
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            Clear selection
          </button>
        </div>
      ) : null}

      {view === 'data' && (
        <ProductionAnalyticsCard
          stats={stats}
          year={year}
          setYear={setYear}
          years={years}
          explain={explain}
          setExplain={setExplain}
        />
      )}

      {view === 'heatmap' && (
        <ProductionHeatmap
          data={heatmapData}
          loading={heatmapState.loading}
          error={heatmapState.error}
          selectedProducts={selectedProducts}
          onSelectProduct={handleSelectProduct}
        />
      )}

      {view === 'line' && (
        <ProductionLineChart
          data={lineData}
          loading={lineState.loading}
          error={lineState.error}
          selectedProducts={selectedProducts}
          onClearSelection={handleClearSelection}
        />
      )}
    </div>
  );
};

export default ProductionData;
